"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Leaf, Wind, Trees, ChartBar as BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface ProgramItem {
  id: number;
  title: string;
  leadUnit: string;
  description: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const programs: ProgramItem[] = [{
  id: 1,
  title: "Phát triển cộng đồng thích ứng biến đổi khí hậu",
  leadUnit: "Phụ trách: CEGTES & Viện IEG",
  description: "Tổ chức tập huấn, đánh giá rủi ro thiên tai tại các địa bàn ven biển và miền núi, giúp cộng đồng chủ động ứng phó với diễn biến thời tiết cực đoan.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/community_climate_resilience.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/community_climate_resilience.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/community_climate_resilience.png-wvc-sizes",
  imageAlt: "Cộng đồng địa phương tham gia tập huấn ứng phó biến đổi khí hậu",
  icon: ShieldCheck
}, {
  id: 2,
  title: "Năng lượng xanh",
  leadUnit: "Phụ trách: Viện IEG",
  description: "Khảo sát tài nguyên gió và bức xạ mặt trời, cung cấp cơ sở dữ liệu địa lý môi trường phục vụ quy hoạch hạ tầng năng lượng tái tạo bền vững.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/vietnam_clean_energy_field.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/vietnam_clean_energy_field.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/vietnam_clean_energy_field.png-wvc-sizes",
  imageAlt: "Trang trại điện gió và pin mặt trời tại vùng duyên hải Việt Nam",
  icon: Wind
}, {
  id: 3,
  title: "Tín chỉ carbon",
  leadUnit: "Phụ trách: CEGTES",
  description: "Đo đếm sinh khối, giám sát trữ lượng rừng tự nhiên và thiết lập phương pháp luận khoa học phục vụ thị trường trao đổi hạn ngạch giảm phát thải.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/forest_carbon_credit_preservation.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/forest_carbon_credit_preservation.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/forest_carbon_credit_preservation.png-wvc-sizes",
  imageAlt: "Cánh rừng nguyên sinh phục vụ đo đếm sinh khối và bảo tồn carbon",
  icon: Trees
}, {
  id: 4,
  title: "Tư vấn ESG",
  leadUnit: "Phụ trách: Viện IEG & CEGTES",
  description: "Hỗ trợ các tổ chức, doanh nghiệp xây dựng khung tiêu chuẩn môi trường, xã hội và quản trị phù hợp với hệ thống pháp luật và quy chuẩn quốc tế.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/esg_consulting_sustainability.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/esg_consulting_sustainability.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/esg_consulting_sustainability.png-wvc-sizes",
  imageAlt: "Buổi làm việc tư vấn tiêu chuẩn phát triển bền vững ESG",
  icon: BarChart3
}, {
  id: 5,
  title: "Môi trường",
  leadUnit: "Phụ trách: CEGTES",
  description: "Nghiên cứu, đánh giá và bảo vệ môi trường sinh thái.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/environment_ecosystem_protection.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/environment_ecosystem_protection-wvc-srcset.png",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/environment_ecosystem_protection-wvc-sizes.png",
  imageAlt: "Nghiên cứu, đánh giá và bảo tồn hệ sinh thái môi trường tự nhiên",
  icon: Leaf
}];
export default function Environment() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.38,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="môi-trường-phát-triển-bền-vững" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with Dual-Bar Framing Motif */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-sm bg-accent text-accent-foreground border border-border text-xs font-semibold uppercase tracking-wider">
            <Leaf className="size-3.5 text-primary" />
            Lĩnh vực nghiên cứu &amp; ứng dụng
          </div>
          
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-secondary font-default">
            Môi trường &amp; Phát triển bền vững
          </h2>

          <div className="relative mt-4 mb-5 flex items-center">
            <div className="w-12 h-1 bg-secondary rounded-xs" />
            <div className="flex-1 h-px bg-border ml-2" />
          </div>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Các chương trình nghiên cứu ứng dụng và triển khai thực địa được điều phối bởi Trung tâm CEGTES và Viện IEG trực thuộc Hội Địa lý môi trường Việt Nam (VAEG).
          </p>
        </div>

        {/* Responsive Grid adjusted for 5 cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((program, index) => {
          const Icon = program.icon;
          return <motion.div key={program.id} variants={itemVariants} data-index={index} className="h-full flex">
                <Card className="group flex-1 flex flex-col justify-between overflow-hidden rounded-sm border-border bg-card hover:border-secondary transition-[border-color,box-shadow] duration-200 shadow-sm hover:shadow-md">
                  {/* Photo container with 1.02x hover zoom */}
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                    <img src={program.imageSrc} data-wvc-srcset={program.imageSrcset} data-wvc-sizes={program.imageSizes} alt={program.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105" />
                    <div className="absolute top-3 left-3 bg-card/90 backdrop-blur-xs px-2.5 py-1 rounded-sm border border-border/80 flex items-center gap-1.5 shadow-2xs">
                      <Icon className="size-3.5 text-primary" />
                      <span className="text-xs font-medium text-foreground">
                        {program.leadUnit}
                      </span>
                    </div>
                  </div>

                  {/* Card Content */}
                  <CardHeader className="p-5 pb-2">
                    <CardTitle className="text-lg font-semibold tracking-tight text-foreground font-default leading-snug group-hover:text-primary transition-colors">
                      {program.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-5 pb-5 pt-0 flex-1 flex flex-col justify-between">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {program.description}
                    </CardDescription>

                    <div className="pt-3 border-t border-border flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        Chương trình thực địa
                      </span>
                      <Button variant="ghost" size="sm" asChild className="h-8 px-2 text-secondary hover:text-secondary-foreground hover:bg-secondary font-medium text-xs gap-1">
                        <Link to="/BusinessAreasPage">
                          Chi tiết
                          <ArrowUpRight className="size-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Footnote citation */}
        <div className="mt-12 p-4 rounded-sm bg-muted/60 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>
            Căn cứ Điều lệ và định hướng hoạt động khoa học công nghệ theo Quyết định số 483/QĐ-BNV và Quyết định số 1157/QĐ-BNV của Bộ Nội vụ.
          </p>
          <Button variant="outline" size="sm" asChild className="shrink-0 border-border text-foreground hover:bg-accent text-xs h-8">
            <Link to="/ContactPage">
              Liên hệ hợp tác nghiên cứu
            </Link>
          </Button>
        </div>

        {/* Closing CTA block */}
        <div className="mt-6 rounded-sm border border-border bg-muted/60 p-4 md:p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm font-medium text-foreground">
            Xem chi tiết cơ cấu tổ chức và các đơn vị phụ trách tại mục Cơ cấu tổ chức
          </p>
          <Button asChild size="sm" className="shrink-0 font-medium text-xs h-9 px-4">
            <Link to="/OrganizationStructurePage">
              Cơ cấu tổ chức
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}